import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosConfig";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";

import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import "../assets/CSS/Dashboard.css";

export default function DeviceApproval() {

  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [kpi, setKpi] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    blocked: 0,
  });

  const [employeeList, setEmployeeList] = useState([]);

  const [filters, setFilters] = useState({
    employee: "",
    status: "ALL",
    search: "",
  });

  /* ================= LOAD DEVICES ================= */

  const loadDevices = async () => {

    try {

      setLoading(true);

      const res = await axiosInstance.get("/api/admin/devices?dashboardType=WIP");

      const data = res.data || [];

      setDevices(data);

      // create employee dropdown list
      const employees = [
        ...new Map(
          data.map((d) => [
            d.EmployeeID,
            { id: d.EmployeeID, name: d.EmployeeName },
          ])
        ).values(),
      ];

      setEmployeeList(employees);

      setKpi({
        total: data.length,
        approved: data.filter((d) => d.Status === "Approved").length,
        pending: data.filter((d) => d.Status === "Pending").length,
        blocked: data.filter((d) => d.Status === "Blocked").length,
      });

    } catch (err) {

      Swal.fire("Error", "Failed to load devices", "error");

    } finally {

      setLoading(false);

    }

  };

  useEffect(() => {
    loadDevices();
  }, []);

  /* ================= ACTIONS ================= */

  const approveDevice = async (deviceId) => {

    const confirm = await Swal.fire({
      title: "Approve Device?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
    });

    if (!confirm.isConfirmed) return;

    try {

      await axiosInstance.post("/api/admin/approve-device", { deviceId });

      Swal.fire("Approved", "Device approved successfully", "success");

      loadDevices();

    } catch {

      Swal.fire("Error", "Approval failed", "error");

    }

  };

  const blockDevice = async (deviceId) => {

    const confirm = await Swal.fire({
      title: "Block Device?",
      text: "User will not be able to login from this device",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Block",
    });

    if (!confirm.isConfirmed) return;

    try {

      await axiosInstance.post("/api/admin/block-device", { deviceId });

      Swal.fire("Blocked", "Device blocked successfully", "success");

      loadDevices();

    } catch {

      Swal.fire("Error", "Block failed", "error");

    }

  };

  const resetFilters = () => {
    setFilters({ employee: "", status: "ALL", search: "" });
  };

  /* ================= FILTER ================= */

  const filteredDevices = devices.filter((d) => {

    if (filters.employee && d.EmployeeID !== filters.employee) return false;

    if (filters.status !== "ALL" && d.Status !== filters.status) return false;

    if (
      filters.search &&
      !(
        d.EmployeeID?.toLowerCase().includes(filters.search.toLowerCase()) ||
        d.EmployeeName?.toLowerCase().includes(filters.search.toLowerCase()) ||
        d.DeviceName?.toLowerCase().includes(filters.search.toLowerCase())
      )
    )
      return false;

    return true;

  });

  /* ================= TABLE STYLE ================= */

  const customStyles = {

    headRow: {
      style: {
        backgroundColor: "#000",
        color: "#fff",
        fontWeight: "600",
        fontSize: "14px",
      },
    },

    rows: {
      style: {
        minHeight: "50px",
      },
    },

  };

  /* ================= LOADER ================= */

  const loader = (
    <div style={{ padding: "40px", textAlign: "center" }}>
      <div
        className="spinner-border text-primary"
        style={{ width: "3rem", height: "3rem" }}
      ></div>
      <div className="mt-2 text-muted">Loading devices...</div>
    </div>
  );

  /* ================= TABLE COLUMNS ================= */

  const columns = [

    {
      name: "#",
      cell: (row, index) => index + 1,
      width: "60px",
    },

    {
      name: "Employee ID",
      selector: (row) => row.EmployeeID,
      sortable: true,
    },

    {
      name: "Employee Name",
      selector: (row) => row.EmployeeName,
      sortable: true,
    },

    {
      name: "Device",
      selector: (row) => row.DeviceName,
      sortable: true,
    },

    {
      name: "Browser",
      cell: (row) => (
        <span style={{ fontSize: "12px" }}>
          {row.Browser ? row.Browser.substring(0, 60) : "-"}
        </span>
      ),
    },

    {
      name: "IP",
      selector: (row) => row.IPAddress,
    },

    {
      name: "Status",
      cell: (row) => {

        if (row.Status === "Approved")
          return <span className="badge bg-success p-2">Approved</span>;

        if (row.Status === "Pending")
          return <span className="badge bg-warning text-dark p-2">Pending</span>;

        if (row.Status === "Blocked")
          return <span className="badge bg-danger p-2">Blocked</span>;

        return <span className="badge bg-secondary p-2">{row.Status}</span>;
      },
      sortable: true,
    },

    {
      name: "Created",
      selector: (row) => new Date(row.CreatedAt).toLocaleString(),
      sortable: true,
    },

    {
      name: "Location",
      selector: row => row.LoginLocation || "-",
      sortable: true
    },

    {
      name: "Last Login",
      cell: row =>
        row.LastLoginAt
          ? new Date(row.LastLoginAt).toLocaleString()
          : "-"
    },

    {
      name: "Online",
      cell: row =>
        row.IsOnline === true
          ? <span className="badge bg-success p-2">Online</span>
          : <span className="badge bg-secondary p-2">Offline</span>
    },

    {
      name: "Action",
      cell: (row) =>
        row.Status !== "Approved" ? (
          <button
            className="btn btn-success btn-sm"
            onClick={() => approveDevice(row.Id)}
          >
            Approve
          </button>
        ) : (
          <button
            className="btn btn-danger btn-sm"
            onClick={() => blockDevice(row.Id)}
          >
            Block
          </button>
        ),
    },

  ];

  return (

    <div className="d-flex dashboard">

      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">

        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-4">

          <h3 className="mb-4 fw-bold">Device Management</h3>

          {/* KPI Cards */}

          <div className="row mb-4">

            {[
              { label: "Total Devices", value: kpi.total, bg: "linear-gradient(135deg,#e0f7ff,#c0eaff)", color: "#007bff" },
              { label: "Approved", value: kpi.approved, bg: "linear-gradient(135deg,#e6f7ec,#b0e6c8)", color: "#28a745" },
              { label: "Pending", value: kpi.pending, bg: "linear-gradient(135deg,#fffbe6,#fff1b0)", color: "#ffc107" },
              { label: "Blocked", value: kpi.blocked, bg: "linear-gradient(135deg,#fdeaea,#f1b0b0)", color: "#dc3545" },
            ].map((k, i) => (

              <div className="col-md-3 mb-3" key={i}>

                <div
                  className="card text-center shadow-sm p-3"
                  style={{
                    background: k.bg,
                    color: k.color,
                    borderRadius: "12px",
                  }}
                >

                  <h6 className="mb-2">{k.label}</h6>

                  <h3 className="mb-0">{k.value}</h3>

                </div>

              </div>

            ))}

          </div>

          {/* Filters */}

          <div className="card p-3 mb-4">

            <div className="row g-3">

              <div className="col-md-4">
                <input
                  className="form-control"
                  placeholder="Search Employee / Device"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                />
              </div>

              {/* EMPLOYEE DROPDOWN */}

              <div className="col-md-4">

                <select
                  className="form-select"
                  value={filters.employee}
                  onChange={(e) =>
                    setFilters({ ...filters, employee: e.target.value })
                  }
                >

                  <option value="">All Employees</option>

                  {employeeList.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.id} - {emp.name}
                    </option>
                  ))}

                </select>

              </div>

              <div className="col-md-2">

                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                >

                  <option value="ALL">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Blocked">Blocked</option>

                </select>

              </div>

              <div className="col-md-2 d-flex gap-2">

                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={resetFilters}
                >
                  Reset
                </button>

                <button
                  className="btn btn-outline-primary w-100"
                  onClick={loadDevices}
                >
                  Refresh
                </button>

              </div>

            </div>

          </div>

          {/* DataTable */}

          <div className="card p-3">

            <DataTable
              columns={columns}
              data={filteredDevices}
              progressPending={loading}
              progressComponent={loader}
              pagination
              highlightOnHover
              dense
              striped
              customStyles={customStyles}
              defaultSortField="CreatedAt"
              defaultSortAsc={false}
            />

          </div>

        </main>

      </div>

    </div>

  );

}